import React, {useEffect, useState, useRef, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Box, CircularProgress, Typography, Button} from '@mui/material';
import NavBar from '../../pieces/navbar/navbar';
import axios from 'axios';
import {REPORTING_API_BASE, reportingHeaders} from '../../utils/reportingApi';
import fetchHierarchyCompanyIds from '../../utils/companyHierarchy';
import {
  PivotViewComponent,
  Inject as PivotInject,
  ExcelExport
} from '@syncfusion/ej2-react-pivotview';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationLegend,
  PieSeries,
  AccumulationDataLabel,
  AccumulationTooltip,
  AccumulationSelection,
  Selection,
  ChartAnnotation,
  AccumulationAnnotation,
  AccumulationAnnotationsDirective,
  AccumulationAnnotationDirective,
  Inject
} from '@syncfusion/ej2-react-charts';
import '@syncfusion/ej2-base/styles/material.css';
//import '@syncfusion/ej2-pivotview/styles/material.css';
import '@syncfusion/ej2-react-pivotview/styles/material.css'; 
//import '@syncfusion/ej2-react-charts/styles/material.css'; 
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';



// Month order mapping for chronological sorting
const monthOrder = {
  'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
  'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
};

// Path and query for permit jobs list (cell links use same origin + this path)
const PERMIT_JOBS_LIST_QUERY = 'moduleName=permit&submodule=all_jobs&items=50&page=1&view=all';

// Status definitions with ID, statusKey, and statusNameMapping
const STATUS_DEFINITIONS = [
  { id: 99, statusKey: 99, statusName: 'Inspection2', statusNameMapping: 'Inspection2' },
  { id: 0, statusKey: 0, statusName: 'Inspection', statusNameMapping: 'Inspection' },
  { id: 13, statusKey: 13, statusName: 'Applied For Refund', statusNameMapping: 'Applied For Refund' },
  { id: 5, statusKey: 5, statusName: 'Appointment Scheduled', statusNameMapping: 'Scheduled' },
  { id: 14, statusKey: 14, statusName: 'Cancelled', statusNameMapping: 'Cancelled' },
  { id: 12, statusKey: 12, statusName: 'Completed', statusNameMapping: 'Completed' },
  { id: 8, statusKey: 8, statusName: 'Failed', statusNameMapping: 'Failed' },
  { id: 11, statusKey: 11, statusName: 'Future Date', statusNameMapping: 'Future Date' },
  { id: 7, statusKey: 7, statusName: 'Hold', statusNameMapping: 'Hold' },
  { id: 6, statusKey: 6, statusName: 'Needs Attention', statusNameMapping: 'Needs Attention' },
  { id: 1, statusKey: 1, statusName: 'New', statusNameMapping: 'New' },
  { id: 9, statusKey: 9, statusName: 'No Result', statusNameMapping: 'No Result' },
  { id: 15, statusKey: 15, statusName: 'Not Required', statusNameMapping: 'Not Required' },
  { id: 2, statusKey: 2, statusName: 'Pending', statusNameMapping: 'Pending' },
  { id: 10, statusKey: 10, statusName: 'Pending City Response', statusNameMapping: 'Pending City Response' },
  { id: 4, statusKey: 4, statusName: 'Pulled Not Uploaded', statusNameMapping: 'Pulled Not Uploaded' },
  { id: 3, statusKey: 3, statusName: 'Submitted', statusNameMapping: 'Submitted' },
  { id: 16, statusKey: 16, statusName: 'No Permit Needed', statusNameMapping: 'No Permit Needed' },
  { id: 17, statusKey: 17, statusName: 'High Priority', statusNameMapping: 'High Priority' },
];

// Helper function to fill missing combinations
const fillMissingCombinations = (data) => {
  if (!data || data.length === 0) return data;

  // Get all unique weeks, companies, and statuses from the data
  // Support both weekName (from API) and monthName (fallback)
  const uniqueWeeks = [...new Set(data.map(item => item.weekName || item.monthName).filter(Boolean))];
  const uniqueCompanies = [...new Set(data.map(item => ({
    companyId: item.companyId,
    companyName: item.companyName
  })).filter(item => item.companyId && item.companyName))];
  
  // Create a map of existing combinations for quick lookup
  const existingCombinations = new Set();
  data.forEach(item => {
    const weekName = item.weekName || item.monthName;
    const key = `${weekName}_${item.companyId}_${item.statusKey}`;
    existingCombinations.add(key);
  });

  // Create missing records
  const missingRecords = [];
  
  uniqueWeeks.forEach(weekName => {
    uniqueCompanies.forEach(company => {
      STATUS_DEFINITIONS.forEach(status => {
        const key = `${weekName}_${company.companyId}_${status.statusKey}`;
        if (!existingCombinations.has(key)) {
          // Format statusName with zero-padded statusKey prefix (e.g., "12.Completed")
          const formattedStatusName = `${String(status.statusKey).padStart(2, '0')}.${status.statusNameMapping}`;
          
          missingRecords.push({
            weekName: weekName,
            companyId: company.companyId,
            companyName: company.companyName,
            statusKey: status.statusKey,
            statusName: formattedStatusName,
            count: 0,
            totalInspection: 0,
            year: data.find(item => (item.weekName || item.monthName) === weekName)?.year || new Date().getFullYear()
          });
        }
      });
    });
  });

  // Ensure all records have weekName field
  const dataWithWeekName = data.map(item => ({
    ...item,
    weekName: item.weekName || item.monthName
  }));

  return [...dataWithWeekName, ...missingRecords];
};

const PermitJobWeeklyAnalytics = () => {
  const company = useSelector(state => state.company);
  const user = useSelector(state => state.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobStatusData, setJobStatusData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  //const [companies, setCompanies] = useState([]);

  const pivotRef = useRef(null);

  // Get company ID from user or company state
  const companyId = user && user.companyId ? user.companyId : (company && company._id ? company._id : 'all');
  
  //console.log('companyId', companyId);
  
  useEffect(() => {


    const fetchData = async () => {
      try {
  
        const compList = user?.userCompanies ?? [];
        const rootIds = compList.length > 0
            ? compList.map(company => company.companyId)
            : [user?.companyId].filter(Boolean);

        // Nothing resolvable yet (the user record is still loading). Bail out
        // rather than querying with an empty list, which the API reads as
        // "every company".
        if (rootIds.length === 0) {
            return;
        }

        // An Enterprise/Primary account holds no jobs itself -- its members do.
        const companies = await fetchHierarchyCompanyIds(rootIds);

        //console.log('compList', compList);
        //console.log('companies', companies);

        const url = `${REPORTING_API_BASE}/FactPermitJob/by-weekly`;
      
        const response = await axios.post(
          url,
          {companies, year: selectedYear},  // Request body (data)
          {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
              ...(await reportingHeaders()),
            }
          }
        ); 

        //console.log('response', response.data);

        // Fill missing combinations with zero counts
        const filledData = fillMissingCombinations(response.data);
        setJobStatusData(filledData);
        setLoading(false);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData().catch(console.error);

  }, [user, selectedYear]);

  // Prepare company dropdown data
  const availableCompanies = useMemo(() => {
    if (!user?.userCompanies || user.userCompanies.length === 0) return [];
    return user.userCompanies.map(company => ({
      text: company.companyName || '',
      value: company.companyId || ''
    }));
  }, [user]);

  // Set default company to first available if not set
  useEffect(() => {
    if (availableCompanies.length > 0 && !selectedCompany) {
      setSelectedCompany(availableCompanies[0].value);
    }
  }, [availableCompanies, selectedCompany]);

  // Extract available months from data
  const availableMonths = useMemo(() => {
    if (!jobStatusData || jobStatusData.length === 0) return [];
    const months = [...new Set(jobStatusData.map(item => item.monthName).filter(Boolean))];
    return months
      .map(month => {
        // Remove numeric prefix like "01.", "02.", etc. from display text
        const cleanMonthName = month.replace(/^\d{2}\.\s*/, '');
        return { 
          text: cleanMonthName, 
          value: month, // Keep original value for filtering
          sortOrder: monthOrder[cleanMonthName] || 999 // Add sort order for sorting
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder) // Sort by chronological order
      .map(({ sortOrder, ...item }) => item); // Remove sortOrder from final result
  }, [jobStatusData]);

  // Set default month to current month if not set
  useEffect(() => {
    if (availableMonths.length > 0 && !selectedMonth) {
      const currentDate = new Date();
      const currentMonthName = currentDate.toLocaleString('default', { month: 'long' }); // e.g., "January"
      
      // Find the month value that matches the current month
      const currentMonthOption = availableMonths.find(month => {
        const cleanMonthName = month.text; // Already cleaned (e.g., "January")
        return cleanMonthName === currentMonthName;
      });
      
      // If current month is found, use it; otherwise use the first available
      setSelectedMonth(currentMonthOption ? currentMonthOption.value : availableMonths[0].value);
    }
  }, [availableMonths, selectedMonth]);

  // Calculate pie chart data based on selected month and company
  useEffect(() => {
    if (!jobStatusData || jobStatusData.length === 0 || !selectedMonth) {
      setPieChartData([]);
      return;
    }

    // Filter data by selected month and company (if company is selected)
    // console.log('=== PIE CHART FILTERING DEBUG ===');
    // console.log('selectedMonth:', selectedMonth);
    // console.log('selectedCompany:', selectedCompany, 'type:', typeof selectedCompany);
    // console.log('Total jobStatusData items:', jobStatusData.length);
    
    // if (jobStatusData.length > 0) {
    //   console.log('Sample item:', jobStatusData[0]);
    //   console.log('Sample item companyId:', jobStatusData[0].companyId, 'type:', typeof jobStatusData[0].companyId);
    //   console.log('Sample item monthName:', jobStatusData[0].monthName);
    //   console.log('Sample item count:', jobStatusData[0].count);
    // }
    
    const filteredData = jobStatusData.filter(item => {
      // Compare monthName - exact match first, then try without prefix
      const itemMonthName = item.monthName || '';
      const selectedMonthClean = selectedMonth || '';
      const monthMatch = itemMonthName === selectedMonthClean || 
                        itemMonthName.replace(/^\d{2}\.\s*/, '') === selectedMonthClean.replace(/^\d{2}\.\s*/, '');
      
      // Compare companyId - handle both string and number types
      let companyMatch = true;
      if (selectedCompany) {
        const itemCompanyId = item.companyId;
        const selectedCompanyId = selectedCompany;
        // Try multiple comparison methods to handle type mismatches
        companyMatch = String(itemCompanyId) === String(selectedCompanyId) ||
                      Number(itemCompanyId) === Number(selectedCompanyId) ||
                      itemCompanyId == selectedCompanyId; // Loose equality for type coercion
        
        // Debug individual item matching
        // if (!companyMatch && itemMonthName === selectedMonthClean) {
        //   console.log('Company mismatch - item.companyId:', itemCompanyId, 'selectedCompany:', selectedCompanyId, 
        //              'String match:', String(itemCompanyId) === String(selectedCompanyId),
        //              'Number match:', Number(itemCompanyId) === Number(selectedCompanyId),
        //              'Loose match:', itemCompanyId == selectedCompanyId);
        // }
      }
      
      return monthMatch && companyMatch;
    });
    
    //console.log('Filtered data count:', filteredData.length);
    if (filteredData.length > 0) {
      //console.log('Sample filtered items (first 3):', filteredData.slice(0, 3));
    } else {
      //console.log('NO DATA MATCHED - checking why...');
      // Check month matches
      const monthMatches = jobStatusData.filter(item => {
        const itemMonthName = item.monthName || '';
        const selectedMonthClean = selectedMonth || '';
        return itemMonthName === selectedMonthClean || 
               itemMonthName.replace(/^\d{2}\.\s*/, '') === selectedMonthClean.replace(/^\d{2}\.\s*/, '');
      });
      //console.log('Items matching month:', monthMatches.length);
      
      // Check company matches
      if (selectedCompany) {
        const companyMatches = jobStatusData.filter(item => {
          const itemCompanyId = item.companyId;
          const selectedCompanyId = selectedCompany;
          return String(itemCompanyId) === String(selectedCompanyId) ||
                 Number(itemCompanyId) === Number(selectedCompanyId) ||
                 itemCompanyId == selectedCompanyId;
        });
        //console.log('Items matching company:', companyMatches.length);
        // if (companyMatches.length > 0) {
        //   console.log('Sample company match:', companyMatches[0]);
        // }
      }
    }
    
    //console.log('Filtered data length:', filteredData.length);
    
    const statusMap = {};
    filteredData.forEach(item => {
      const statusName = item.statusName || '';
      // Remove "01.", "02." etc. prefix if present
      const cleanStatusName = statusName.replace(/^\d{2}\.\s*/, '');
      const count = item.count || 0;
      
      if (statusMap[cleanStatusName]) {
        statusMap[cleanStatusName] += count;
      } else {
        statusMap[cleanStatusName] = count;
      }
    });
    
    // console.log('Status map:', statusMap);
    // console.log('Status map keys:', Object.keys(statusMap));
    
    // Convert to array format for pie chart, filtering out statuses with count = 0
    // Sort by statusKey to match PivotViewComponent column header order
    const chartData = Object.keys(statusMap)
      .filter(statusName => statusMap[statusName] > 0) // Filter out statuses with count = 0
      .map(statusName => {
        // Find the statusKey for this statusName from STATUS_DEFINITIONS
        const statusDef = STATUS_DEFINITIONS.find(def => def.statusNameMapping === statusName);
        const statusKey = statusDef ? statusDef.statusKey : 999; // Use 999 as fallback for unknown statuses
        return {
          x: statusName,
          y: statusMap[statusName],
          statusKey: statusKey
        };
      })
      .sort((a, b) => a.statusKey - b.statusKey) // Sort by statusKey to match column header order
      .map(({ statusKey, ...item }) => item); // Remove statusKey from final data
    
    // Calculate percentages
    const totalSum = chartData.reduce((sum, item) => sum + item.y, 0);
    const chartDataWithPercentages = chartData.map((item) => {
      const percentage = totalSum > 0 ? parseFloat(((item.y / totalSum) * 100).toFixed(2)) : 0;
      const labelText = `${percentage.toFixed(2)}%`;
      return {
        ...item,
        y: percentage,
        label: labelText,
        text: labelText
      };
    });
    
    //console.log('chartDataWithPercentages', chartDataWithPercentages);
    
    setPieChartData(chartDataWithPercentages);
  }, [jobStatusData, selectedMonth, selectedCompany]);

  // Transform data to use weekName, add Inspection records, and sort
  const transformedJobStatusData = useMemo(() => {
    if (!jobStatusData || jobStatusData.length === 0) return [];
    
    // First, sum totalInspection values by week/company
    const inspectionMap = {};
    
    jobStatusData.forEach(item => {
      const weekName = item.weekName || item.monthName || '';
      const companyId = item.companyId;
      const key = `${weekName}_${companyId}`;
      
      if (!inspectionMap[key]) {
        inspectionMap[key] = {
          weekName: weekName,
          companyId: item.companyId,
          companyName: item.companyName,
          year: item.year,
          totalInspection: 0
        };
      }
      // Sum the totalInspection values for each week/company
      inspectionMap[key].totalInspection += (item.totalInspection || 0);
    });
    
    // Create "Inspection" records from summed totalInspection values
    const inspectionRecords = Object.values(inspectionMap).map(item => ({
      ...item,
      statusName: '00.Inspection',
      statusKey: 0,
      count: item.totalInspection
    }));
    
    // Create "Inspection2" records with negative values ("00.Inspection" * -1)
    const inspection2Records = Object.values(inspectionMap).map(item => ({
      ...item,
      statusName: '99.Inspection2',
      statusKey: 99,
      count: -1 * item.totalInspection // Negative value to cancel out in Grand Total
    }));
    
    // Transform existing records to ensure weekName is present
    const transformedRecords = jobStatusData
      .map(item => ({
        ...item,
        weekName: item.weekName || item.monthName || ''
      }));
    
    // Combine and sort (includes inspection2Records which will be hidden but affects Grand Total)
    return [...inspectionRecords, ...inspection2Records, ...transformedRecords].sort((a, b) => {
      // First sort by company name
      const companyCompare = (a.companyName || '').localeCompare(b.companyName || '');
      if (companyCompare !== 0) return companyCompare;
      // Then sort by week name
      const weekA = a.weekName || '';
      const weekB = b.weekName || '';
      return weekA.localeCompare(weekB);
    });
  }, [jobStatusData]);

  // Generate years from 2020 to 2028
  const availableYears = useMemo(() => {
    const years = [];
    for (let year = 2028; year >= 2020; year--) {
      years.push({ text: year.toString(), value: year });
    }
    return years;
  }, []);

  // // Set default year to 2024 (or most recent available in data)
  // useEffect(() => {
  //   if (!selectedYear && availableYears.length > 0) {
  //     // Check if data has years and set to most recent, otherwise default to 2024
  //     if (jobStatusData && jobStatusData.length > 0) {
  //       const dataYears = [...new Set(jobStatusData.map(item => item.year).filter(Boolean))];
  //       if (dataYears.length > 0) {
  //         const mostRecent = Math.max(...dataYears);
  //         setSelectedYear(mostRecent);
  //       } else {
  //         setSelectedYear(2024);
  //       }
  //     } else {
  //       setSelectedYear(2024);
  //     }
  //   }
  // }, [availableYears, selectedYear, jobStatusData]);

  // Filter data by selected year
  // const filteredJobStatusData = useMemo(() => {
  //   if (!selectedYear || !jobStatusData || jobStatusData.length === 0) return jobStatusData;
  //   return jobStatusData.filter(item => item.year === selectedYear);
  // }, [jobStatusData, selectedYear]);


  useEffect(() => {
    if (!jobStatusData || jobStatusData.length === 0) return;

    // Map company name -> companyId for building cell links (normalize key by trim for lookup)
    // Support both companyId/CompanyId and companyName/CompanyName from API payload
    const companyNameToId = {};
    const companyIdToName = {}; // id -> name (pivot may show id in row header)
    jobStatusData.forEach((item) => {
      const name = item.companyName ?? item.CompanyName;
      const id = item.companyId ?? item.CompanyId;
      if (name != null && id != null) {
        const key = String(name).trim();
        companyNameToId[key] = id;
        companyIdToName[String(id)] = key;
        companyIdToName[id] = key;
      }
    });

    const cleanupHeaders = () => {
      const pivotView = document.getElementById('FactPermitJobPivot');
      if (!pivotView) return;

      // Find all header cells - target column headers specifically
      const columnHeaders = pivotView.querySelectorAll('.e-columnsheader .e-headercell, .e-columnsheader .e-headercelldiv, .e-columnsheader th, .e-columnsheader .e-headercell span, .e-columnsheader .e-headercelldiv span');
      columnHeaders.forEach(header => {
        if (!header || !header.textContent) return;
        
        let text = (header.textContent || '').trim();
        const originalText = text;
        
        // Remove patterns like "01.", "02.", etc. from the beginning
        text = text.replace(/^\d{2}\.\s*/i, '');
        
        if (text !== originalText && text.length > 0) {
          header.textContent = text;
        }
      });
    };

    // Hide "Inspection2" column in PivotViewComponent
    const hideInspection2Column = () => {
      const pivotView = document.getElementById('FactPermitJobPivot');
      if (!pivotView) return;

      // Find column index by searching all header elements
      let inspection2ColIndex = -1;
      
      // Search in the last header row (where column names are)
      const headerRow = pivotView.querySelector('.e-gridheader table thead tr:last-child');
      if (headerRow) {
        const headerCells = headerRow.querySelectorAll('th');
        headerCells.forEach((cell, index) => {
          const text = cell.textContent || '';
          if (text.includes('Inspection2') || text.includes('99.Inspec') || text.includes('99-Inspec')) {
            inspection2ColIndex = index;
            // Hide the header cell
            cell.style.setProperty('display', 'none', 'important');
            cell.style.setProperty('width', '0', 'important');
            cell.style.setProperty('min-width', '0', 'important');
            cell.style.setProperty('max-width', '0', 'important');
            cell.style.setProperty('padding', '0', 'important');
            cell.style.setProperty('border', 'none', 'important');
            cell.style.setProperty('visibility', 'hidden', 'important');
          }
        });
      }

      // Also search all header divs for the text
      const allHeaderDivs = pivotView.querySelectorAll('.e-headercell, .e-headercelldiv, .e-columnsheader th, .e-columnsheader .e-headercell');
      allHeaderDivs.forEach((el) => {
        const text = el.textContent || '';
        if (text.includes('Inspection2') || text.includes('99.Inspec') || text.includes('99-Inspec')) {
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
          // Also hide parent th if exists
          const parentTh = el.closest('th');
          if (parentTh) {
            parentTh.style.setProperty('display', 'none', 'important');
            parentTh.style.setProperty('width', '0', 'important');
            parentTh.style.setProperty('visibility', 'hidden', 'important');
          }
        }
      });

      // Hide body cells in the same column
      if (inspection2ColIndex >= 0) {
        const bodyTable = pivotView.querySelector('.e-content table, .e-gridcontent table');
        if (bodyTable) {
          const bodyRows = bodyTable.querySelectorAll('tbody tr');
          bodyRows.forEach(row => {
            const cells = row.querySelectorAll('td');
            if (cells[inspection2ColIndex]) {
              cells[inspection2ColIndex].style.setProperty('display', 'none', 'important');
              cells[inspection2ColIndex].style.setProperty('width', '0', 'important');
              cells[inspection2ColIndex].style.setProperty('visibility', 'hidden', 'important');
            }
          });
        }
      }
    };

    // Style "00.Inspection" column values with green color
    const styleInspectionColumn = () => {
      const pivotView = document.getElementById('FactPermitJobPivot');
      if (!pivotView) return;

      // Find column index for "Inspection" (not "Inspection2")
      let inspectionColIndex = -1;
      
      const headerRow = pivotView.querySelector('.e-gridheader table thead tr:last-child');
      if (headerRow) {
        const headerCells = headerRow.querySelectorAll('th');
        headerCells.forEach((cell, index) => {
          const text = cell.textContent || '';
          // Match "Inspection" but not "Inspection2"
          if ((text.includes('Inspection') && !text.includes('Inspection2')) ||
              (text.includes('00.Inspec') && !text.includes('99.'))) {
            inspectionColIndex = index;
            // Also style the header green
            cell.style.setProperty('color', 'green', 'important');
            cell.style.setProperty('font-weight', '600', 'important');
          }
        });
      }

      // Style body cells in the "Inspection" column with green
      if (inspectionColIndex >= 0) {
        const bodyTable = pivotView.querySelector('.e-content table, .e-gridcontent table');
        if (bodyTable) {
          const bodyRows = bodyTable.querySelectorAll('tbody tr');
          bodyRows.forEach(row => {
            const cells = row.querySelectorAll('td');
            if (cells[inspectionColIndex]) {
              cells[inspectionColIndex].style.setProperty('color', 'green', 'important');
              cells[inspectionColIndex].style.setProperty('font-weight', '600', 'important');
            }
          });
        }
      }
    };

    // Add percentage to each cell based on row's total (excluding certain statuses)
    const addPercentagesToCells = () => {
      const pivotView = document.getElementById('FactPermitJobPivot');
      if (!pivotView) return;

      // Statuses to exclude from total calculation
      const excludedStatuses = ['Cancelled', 'No Permit Needed', 'Not Required', 'Inspection', 'Inspection2'];

      // Get header row to identify column indices to exclude and to get statusName per column
      const headerRow = pivotView.querySelector('.e-gridheader table thead tr:last-child');
      const headerCells = headerRow ? headerRow.querySelectorAll('th') : [];
      const excludedColumnIndices = new Set();
      
      if (headerRow) {
        headerCells.forEach((cell, index) => {
          const headerText = (cell.textContent || '').trim();
          // Check if this header matches any excluded status
          if (excludedStatuses.some(status => headerText.includes(status))) {
            excludedColumnIndices.add(index);
          }
        });
      }

      const bodyTable = pivotView.querySelector('.e-content table, .e-gridcontent table');
      if (!bodyTable) return;

      const bodyRows = Array.from(bodyTable.querySelectorAll('tbody tr'));
      const monthNames = Object.keys(monthOrder);
      let currentCompanyName = null;
      // Pivot has 2 row dimensions (companyName, monthName) so first 2 columns are row headers
      const ROW_HEADER_COLUMN_COUNT = 2;

      // Row dimensions: column 0 = Company, column 1 = Week. Company name for a week row comes from the parent row's Company column.
      const COMPANY_COLUMN_INDEX = 0;
      const WEEK_COLUMN_INDEX = 1;

      bodyRows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('td');
        if (!cells || cells.length <= ROW_HEADER_COLUMN_COUNT) return;

        // Week from Week column - use cell0 for weekForRow
        let weekForRow = null;
        const cell1 = (cells[WEEK_COLUMN_INDEX]?.textContent || '').trim();
        const cell0 = (cells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
        // Get weekName from cell0 - it should contain the week identifier
        // Check if cell0 looks like a week (starts with "Week of")
        if (cell0 && cell0.trim() && /^week\s+of\s+/i.test(cell0.trim())) {
          weekForRow = cell0.trim();
        }

        // Company name from parent row's Company column (index 0) when on a week row
        // This companyName will be used for all column cells in this row when creating links
        if (weekForRow) {
          currentCompanyName = null;
          // Look backwards to find the direct parent company row (has company name but no month)
          // This ensures we get the company name from the correct parent, not from a previous month's row
          for (let p = rowIndex - 1; p >= 0; p--) {
            const prevRow = bodyRows[p];
            if (!prevRow) break;
            const prevCells = prevRow.querySelectorAll('td');
            const prevCompanyCellText = (prevCells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
            const prevWeekCellText = (prevCells[WEEK_COLUMN_INDEX]?.textContent || '').trim();
            
            // If week column has a week name (starts with "Week of"), this is a week row, not a company row - skip it
            const prevWeekIsWeek = prevWeekCellText && /^week\s+of\s+/i.test(prevWeekCellText.trim());
            
            // Skip if this is a week row (has week in column 1)
            if (prevWeekIsWeek) continue;
            
            // Legacy check for month names (for backward compatibility)
            const prevMonthIsMonth = prevWeekCellText && monthNames.some(m => {
              const trimmed = prevWeekCellText.trim();
              return trimmed === m || 
                     trimmed.startsWith(m + ' ') || 
                     trimmed.endsWith(' ' + m) ||
                     (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
            });
            
            // Skip if this is a month row (has month in column 1) - already handled above
            if (prevMonthIsMonth) continue;
            
            // Check if company cell text is actually a month name (should not be treated as company)
            // Use exact match or starts/ends with to avoid false positives
            const companyCellIsMonth = prevCompanyCellText && monthNames.some(m => {
              const trimmed = prevCompanyCellText.trim();
              return trimmed === m || 
                     trimmed.startsWith(m + ' ') || 
                     trimmed.endsWith(' ' + m) ||
                     (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
            });
            
            // Skip if company cell text is a month name
            if (companyCellIsMonth) continue;
            
            // Check if the company cell text is a valid company name (exists in companyNameToId mapping)
            // First try exact match, then try trimmed match, then try partial match
            const isValidCompanyName = prevCompanyCellText && (
              companyNameToId[prevCompanyCellText] != null ||
              companyNameToId[prevCompanyCellText.trim()] != null ||
              Object.keys(companyNameToId).some(name => {
                const nameTrimmed = name.trim();
                const cellTrimmed = prevCompanyCellText.trim();
                return nameTrimmed === cellTrimmed || 
                       nameTrimmed.toLowerCase() === cellTrimmed.toLowerCase() ||
                       (cellTrimmed.length > 5 && nameTrimmed.includes(cellTrimmed)) ||
                       (nameTrimmed.length > 5 && cellTrimmed.includes(nameTrimmed));
              })
            );
            
            // This is a company header row if it has a valid company name and no month
            const isCompanyRow = prevCompanyCellText && isValidCompanyName;
            
            if (isCompanyRow) {
              currentCompanyName = prevCompanyCellText;
              break;
            }
          }
          // If we still don't have a company name, try using cell0 directly if it's not a week
          if (!currentCompanyName && cell0 && !weekForRow) {
            const cell0Text = cell0.trim();
            // Check if cell0 is a valid company name (not a week, not a month, not "Total")
            if (cell0Text && 
                !/^week\s+of\s+/i.test(cell0Text) &&
                !cell0Text.toLowerCase().includes('total') &&
                !monthNames.some(m => {
                  const trimmed = cell0Text.trim();
                  return trimmed === m || 
                         trimmed.startsWith(m + ' ') || 
                         trimmed.endsWith(' ' + m) ||
                         (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
                })) {
              // Check if it's a valid company name
              if (companyNameToId[cell0Text] || companyNameToId[cell0Text.trim()] ||
                  Object.keys(companyNameToId).some(name => {
                    const nameTrimmed = name.trim();
                    const cellTrimmed = cell0Text.trim();
                    return nameTrimmed === cellTrimmed || 
                           nameTrimmed.toLowerCase() === cellTrimmed.toLowerCase();
                  })) {
                currentCompanyName = cell0Text;
              }
            }
          }
        } else {
          // Not a week row - could be a company row or a total row
          const companyCellText = (cells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
          const weekCellText = (cells[WEEK_COLUMN_INDEX]?.textContent || '').trim();
          
          // Check if this is a total row (has "Total" text or month name in company column)
          const isTotalRow = companyCellText && (
            companyCellText.toLowerCase().includes('total') ||
            monthNames.some(m => {
              const trimmed = companyCellText.trim();
              return trimmed === m || 
                     trimmed.startsWith(m + ' ') || 
                     trimmed.endsWith(' ' + m) ||
                     (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
            })
          );
          
          if (isTotalRow) {
            // For total rows, look backwards to find the parent company row
            currentCompanyName = null;
            for (let p = rowIndex - 1; p >= 0; p--) {
              const prevRow = bodyRows[p];
              if (!prevRow) break;
              const prevCells = prevRow.querySelectorAll('td');
              const prevCompanyCellText = (prevCells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
              const prevWeekCellText = (prevCells[WEEK_COLUMN_INDEX]?.textContent || '').trim();
              
              // If week column has a week name (starts with "Week of"), this is a week row, not a company row - skip it
              const prevWeekIsWeek = prevWeekCellText && /^week\s+of\s+/i.test(prevWeekCellText.trim());
              
              // Skip if this is a week row (has week in column 1)
              if (prevWeekIsWeek) continue;
              
              // Legacy check for month names (for backward compatibility)
              const prevMonthIsMonth = prevWeekCellText && monthNames.some(m => {
                const trimmed = prevWeekCellText.trim();
                return trimmed === m || 
                       trimmed.startsWith(m + ' ') || 
                       trimmed.endsWith(' ' + m) ||
                       (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
              });
              
              // Skip if this is a month row (has month in column 1) - already handled above
              if (prevMonthIsMonth) continue;
              
              // Check if company cell text is actually a month name (should not be treated as company)
              const companyCellIsMonth = prevCompanyCellText && monthNames.some(m => {
                const trimmed = prevCompanyCellText.trim();
                return trimmed === m || 
                       trimmed.startsWith(m + ' ') || 
                       trimmed.endsWith(' ' + m) ||
                       (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
              });
              
              // Skip if company cell text is a month name or "Total"
              if (companyCellIsMonth || prevCompanyCellText.toLowerCase().includes('total')) continue;
              
              // Check if the company cell text is a valid company name (exists in companyNameToId mapping)
              const isValidCompanyName = prevCompanyCellText && (
                companyNameToId[prevCompanyCellText] != null ||
                companyNameToId[prevCompanyCellText.trim()] != null ||
                Object.keys(companyNameToId).some(name => {
                  const nameTrimmed = name.trim();
                  const cellTrimmed = prevCompanyCellText.trim();
                  return nameTrimmed === cellTrimmed || 
                         nameTrimmed.toLowerCase() === cellTrimmed.toLowerCase() ||
                         (cellTrimmed.length > 5 && nameTrimmed.includes(cellTrimmed)) ||
                         (nameTrimmed.length > 5 && cellTrimmed.includes(nameTrimmed));
                })
              );
              
              // This is a company header row if it has a valid company name and no month
              const isCompanyRow = prevCompanyCellText && isValidCompanyName;
              
              if (isCompanyRow) {
                currentCompanyName = prevCompanyCellText;
                break;
              }
            }
          } else {
            // Regular company row - check if company cell text is actually a month name (use strict matching)
            const companyCellIsMonth = companyCellText && monthNames.some(m => {
              const trimmed = companyCellText.trim();
              return trimmed === m || 
                     trimmed.startsWith(m + ' ') || 
                     trimmed.endsWith(' ' + m) ||
                     (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
            });
            // Check if it's a valid company name
            const isValidCompanyName = companyCellText && (
              companyNameToId[companyCellText] != null ||
              companyNameToId[companyCellText.trim()] != null ||
              Object.keys(companyNameToId).some(name => {
                const nameTrimmed = name.trim();
                const cellTrimmed = companyCellText.trim();
                return nameTrimmed === cellTrimmed || 
                       nameTrimmed.toLowerCase() === cellTrimmed.toLowerCase() ||
                       (cellTrimmed.length > 5 && nameTrimmed.includes(cellTrimmed)) ||
                       (nameTrimmed.length > 5 && cellTrimmed.includes(nameTrimmed));
              })
            );
            if (companyCellText && !companyCellIsMonth && isValidCompanyName) {
              currentCompanyName = companyCellText;
            }
          }
        }

        // Calculate custom total excluding specific status columns
        let customTotal = 0;
        cells.forEach((cell, index) => {
          // Skip row header columns, last column (Grand Total), and excluded columns
          if (index < ROW_HEADER_COLUMN_COUNT || index === cells.length - 1) return;
          if (excludedColumnIndices.has(index)) return;

          const cellText = cell.textContent || '';
          const cellMatch = cellText.match(/^[\s]*([\d,]+)/);
          const cellValue = cellMatch ? parseFloat(cellMatch[1].replace(/,/g, '')) : 0;
          if (!isNaN(cellValue)) {
            customTotal += cellValue;
          }
        });

        // Skip if custom total is 0 or invalid
        if (customTotal === 0 || isNaN(customTotal)) return;

        // Look up companyId by exact or partial match (trimmed, case-insensitive)
        let companyIdForRow = null;
        if (currentCompanyName) {
          const key = currentCompanyName.trim();
          companyIdForRow = companyNameToId[key] ?? companyNameToId[currentCompanyName];
          if (companyIdForRow == null) {
            const match = Object.keys(companyNameToId).find(
              (name) => key.includes(name) || name.includes(key)
            );
            if (match) companyIdForRow = companyNameToId[match];
          }
          if (companyIdForRow == null) {
            const keyLower = key.toLowerCase();
            const match = Object.keys(companyNameToId).find(
              (name) => {
                const nameLower = name.toLowerCase();
                return keyLower === nameLower || keyLower.includes(nameLower) || nameLower.includes(keyLower);
              }
            );
            if (match) companyIdForRow = companyNameToId[match];
          }
        }

        // Process each data cell (skip row header columns)
        cells.forEach((cell, index) => {
          if (index < ROW_HEADER_COLUMN_COUNT) return;
          if (cell.querySelector('.cell-percentage')) return;

          const cellText = cell.textContent || '';
          // Skip if cell is empty or just whitespace
          if (!cellText.trim()) return;

          // Extract the numeric value from cell
          const cellMatch = cellText.match(/^[\s]*([\d,]+)/);
          const cellValue = cellMatch ? parseFloat(cellMatch[1].replace(/,/g, '')) : 0;

          // Skip if value is NaN (but allow 0 values for alignment)
          if (isNaN(cellValue)) return;

          // Calculate percentage using custom total (excluding certain statuses)
          const percentage = ((cellValue / customTotal) * 100).toFixed(1);

          // Create percentage span
          const percentSpan = document.createElement('span');
          percentSpan.className = 'cell-percentage';
          percentSpan.style.cssText = 'font-size:12px;color:#666;display:block;';
          percentSpan.textContent = `(${percentage}%)`;

          // Clear cell and add value with percentage
          cell.innerHTML = '';
          const valueSpan = document.createElement('span');
          valueSpan.style.cssText = 'font-size:14px;font-weight:500;';
          valueSpan.textContent = cellValue.toLocaleString();

          // Wrap in clickable link when we have a week for this row (same pattern as inspection view: same origin, open in new tab)
          if (weekForRow) {
            const link = document.createElement('a');
            const params = new URLSearchParams();
            // Extract date from weekName by removing "Week of " prefix
            // If weekName is "Week of 2025-12-28", extract "2025-12-28"
            if (weekForRow && weekForRow.trim() && /^week\s+of\s+/i.test(weekForRow)) {
              // Remove "Week of " prefix (case-insensitive) and trim
              let dateString = weekForRow.replace(/^week\s+of\s+/i, '').trim();
              // Parse the date string
              if (dateString) {
                try {
                  // Try parsing as yyyy-MM-dd format first
                  const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
                  const match = dateString.match(datePattern);
                  if (match) {
                    // Parse the date components to validate
                    const year = parseInt(match[1], 10);
                    const month = parseInt(match[2], 10);
                    const day = parseInt(match[3], 10);
                    const parsedDate = new Date(year, month - 1, day);
                    // Validate the parsed date matches the input
                    if (!isNaN(parsedDate.getTime()) && 
                        parsedDate.getFullYear() === year && 
                        parsedDate.getMonth() === month - 1 && 
                        parsedDate.getDate() === day) {
                      params.set('currentWeek', dateString);
                    }
                  } else {
                    // Not in yyyy-MM-dd format, try to parse and format
                    const parsedDate = new Date(dateString);
                    if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 2000) {
                      const year = parsedDate.getFullYear();
                      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
                      const day = String(parsedDate.getDate()).padStart(2, '0');
                      params.set('currentWeek', `${year}-${month}-${day}`);
                    }
                  }
                } catch (e) {
                  // If parsing fails, don't set currentWeek
                }
              }
            }
            // companyName from parent row (header of week column); resolve ID to name if pivot shows ID
            if (currentCompanyName) {
              const companyNameForUrl = companyIdToName[currentCompanyName] ?? companyIdToName[String(currentCompanyName)] ?? currentCompanyName.replace(/\s+/g, ' ').trim();
              if (companyNameForUrl) params.set('companyName', companyNameForUrl);
            }
            if (companyIdForRow != null && companyIdForRow !== '') {
              params.set('companyId', String(companyIdForRow));
            }
            // Add statusName from column header (header row may align with body, or status columns only)
            const headerCell = headerCells[index] || headerCells[index - ROW_HEADER_COLUMN_COUNT];
            const columnHeaderText = (headerCell?.textContent || '').trim();
            const statusDef = columnHeaderText && STATUS_DEFINITIONS.find(
              (d) =>
                columnHeaderText.includes(d.statusName) ||
                columnHeaderText.includes(d.statusNameMapping) ||
                d.statusName.includes(columnHeaderText) ||
                d.statusNameMapping.includes(columnHeaderText)
            );
            if (statusDef) {
              params.set('statusName', statusDef.statusName);
            } else if (columnHeaderText) {
              params.set('statusName', columnHeaderText);
            }
            // Use + for spaces in query string so company name appears as text (not %20)
            const url = `${window.location.origin}/module?${PERMIT_JOBS_LIST_QUERY}&${params.toString().replace(/%20/g, '+')}`;
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.style.cssText = 'color:inherit;text-decoration:none;cursor:pointer;';
            link.appendChild(valueSpan);
            link.appendChild(percentSpan);
            link.addEventListener('click', (e) => {
              e.preventDefault();
              window.open(url, '_blank');
            });
            cell.appendChild(link);
          } else {
            cell.appendChild(valueSpan);
            cell.appendChild(percentSpan);
          }
        });
      });
    };

    // Hide percentage spans in Grand Total cells (e-gtot class)
    const hideGrandTotalPercentages = () => {
      const pivotView = document.getElementById('FactPermitJobPivot');
      if (!pivotView) return;
      
      const grandTotalCells = pivotView.querySelectorAll('td.e-gtot');
      grandTotalCells.forEach(cell => {
        const percentageSpans = cell.querySelectorAll('span.cell-percentage');
        percentageSpans.forEach(span => {
          span.style.display = 'none';
        });
      });
    };

    // Force first column width to 200px (override pivot table component)
    const forceFirstColumnWidth = () => {
      const pivotView = document.getElementById('FactPermitJobPivot');
      if (!pivotView) return;

      // Target all possible first column elements
      const firstColumnSelectors = [
        'table thead th:nth-child(1)',
        'table tbody td:nth-child(1)',
        '.e-rowsheader th:nth-child(1)',
        '.e-rowsheader td:nth-child(1)',
        'table tbody td.e-rowsheader:nth-child(1)',
        '.e-gridcell:nth-child(1)',
        '.e-headercell:nth-child(1)',
        '.e-gridheader th:nth-child(1)',
        '.e-content td:nth-child(1)'
      ];

      firstColumnSelectors.forEach(selector => {
        const elements = pivotView.querySelectorAll(selector);
        elements.forEach(el => {
          if (el) {
            el.style.setProperty('width', '200px', 'important');
            el.style.setProperty('min-width', '200px', 'important');
            el.style.setProperty('max-width', '200px', 'important');
          }
        });
      });
    };

    // Run immediately
    cleanupHeaders();
    hideInspection2Column();
    styleInspectionColumn();
    addPercentagesToCells();
    hideGrandTotalPercentages();
    forceFirstColumnWidth();
    requestAnimationFrame(() => {
      cleanupHeaders();
      hideInspection2Column();
      styleInspectionColumn();
      addPercentagesToCells();
      hideGrandTotalPercentages();
      forceFirstColumnWidth();
      requestAnimationFrame(() => {
        cleanupHeaders();
        hideInspection2Column();
        styleInspectionColumn();
        addPercentagesToCells();
        hideGrandTotalPercentages();
        forceFirstColumnWidth();
      });
    });

    // Use MutationObserver to catch dynamic updates
    const observer = new MutationObserver(() => {
      cleanupHeaders();
      hideInspection2Column();
      styleInspectionColumn();
      addPercentagesToCells();
      hideGrandTotalPercentages();
      forceFirstColumnWidth();
      requestAnimationFrame(() => {
        cleanupHeaders();
        hideInspection2Column();
        styleInspectionColumn();
        addPercentagesToCells();
        hideGrandTotalPercentages();
        forceFirstColumnWidth();
        requestAnimationFrame(() => {
          cleanupHeaders();
          hideInspection2Column();
          styleInspectionColumn();
          addPercentagesToCells();
          hideGrandTotalPercentages();
          forceFirstColumnWidth();
        });
      });
    });

    const pivotView = document.getElementById('FactPermitJobPivot');
    if (pivotView) {
      observer.observe(pivotView, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [jobStatusData, pieChartData, selectedYear]); // Re-run when year changes for currentWeek date

  // Load function for the pie chart
  const loadChart = (args) => {
    // Chart load customization if needed
  };

  // Prevent label truncation and ensure all labels are visible by manipulating SVG text elements
  useEffect(() => {
    if (!pieChartData || pieChartData.length === 0) return;

    const ensureAllLabelsVisible = () => {
      const chartElement = document.getElementById('pie-chart2');
      if (!chartElement) return;

      // Find all data label groups and ensure they are visible
      const dataLabelGroups = chartElement.querySelectorAll('[id*="datalabel"], [class*="datalabel"], g[id*="label"]');
      dataLabelGroups.forEach((group) => {
        group.style.display = 'block';
        group.style.visibility = 'visible';
        group.style.opacity = '1';
        group.removeAttribute('display');
      });

      // Find all data label text elements and fix blank ones
      const dataLabelTexts = chartElement.querySelectorAll('[id*="datalabel"] text, [class*="datalabel"] text, g[id*="Series"] text');
      let labelIndex = 0;
      
      dataLabelTexts.forEach((textEl) => {
        // Force visibility
        textEl.style.display = 'block';
        textEl.style.visibility = 'visible';
        textEl.style.opacity = '1';
        textEl.removeAttribute('display');
        textEl.style.textOverflow = 'clip';
        textEl.style.overflow = 'visible';
        textEl.removeAttribute('textLength');
        textEl.removeAttribute('lengthAdjust');
        
        const text = (textEl.textContent || '').trim();
        
        // Fix blank or invalid labels
        if ((!text || text === '' || text.length <= 1) && pieChartData && pieChartData.length > 0) {
          // Try to assign a label from the data based on index
          if (labelIndex < pieChartData.length && pieChartData[labelIndex].label) {
            textEl.textContent = pieChartData[labelIndex].label;
          }
          labelIndex++;
        } else if (text.includes('...')) {
          // Fix truncated labels
          const matchingData = pieChartData.find(d => d.label && d.label.startsWith(text.replace('...', '')));
          if (matchingData && matchingData.label) {
            textEl.textContent = matchingData.label;
          }
        } else if (text.length <= 2 && !text.includes('%') && pieChartData && pieChartData.length > 0) {
          // Single digit - might be truncated percentage
          const matchingData = pieChartData.find(d => d.label && d.label.startsWith(text));
          if (matchingData && matchingData.label) {
            textEl.textContent = matchingData.label;
          }
        }
      });
      
      // Also check all text elements
      const allTextElements = chartElement.querySelectorAll('text, tspan');
      allTextElements.forEach((textEl) => {
        textEl.style.display = 'block';
        textEl.style.visibility = 'visible';
        textEl.style.opacity = '1';
        if (textEl.style.display === 'none') {
          textEl.style.display = 'block';
        }
      });

      // Also check for hidden label groups and paths
      const labelGroups = chartElement.querySelectorAll('[class*="datalabel"], [class*="label"], g[class*="label"]');
      labelGroups.forEach((group) => {
        if (group.style.display === 'none' || group.style.visibility === 'hidden') {
          group.style.display = 'block';
          group.style.visibility = 'visible';
        }
        group.style.opacity = '1';
      });

      // Check for any elements with display:none in computed styles
      const allElements = chartElement.querySelectorAll('*');
      allElements.forEach((el) => {
        if (el.tagName === 'text' || el.tagName === 'tspan' || el.classList.toString().includes('label') || el.classList.toString().includes('datalabel')) {
          const computedStyle = window.getComputedStyle(el);
          if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
            el.style.display = 'block';
            el.style.visibility = 'visible';
          }
        }
      });
    };

    // Run immediately and after delays to catch dynamic updates
    ensureAllLabelsVisible();
    const timeout1 = setTimeout(ensureAllLabelsVisible, 100);
    const timeout2 = setTimeout(ensureAllLabelsVisible, 500);
    const timeout3 = setTimeout(ensureAllLabelsVisible, 1000);

    // Use MutationObserver to catch when labels are added or modified
    const chartElement = document.getElementById('pie-chart2');
    if (chartElement) {
      const observer = new MutationObserver(() => {
        ensureAllLabelsVisible();
      });

      observer.observe(chartElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'display', 'visibility', 'opacity']
      });

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
        observer.disconnect();
      };
    }

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [pieChartData]);

  const onHeaderCellInfo = (args) => {
    const col = args.cell?.column;
    if (!col) return;
  
    if (col.index === 0) {
      // first column: company + month
      col.width = 200
    } else {
      // metric columns - ensure consistent width
      col.width = 50;
    }

    if (args.node) {
      args.node.style.width = col.width + 'px';
      args.node.style.minWidth = col.width + 'px';
      args.node.style.maxWidth = col.width + 'px';
    }
  };


  // Excel export complete handler
  const excelExportComplete = (args) => {
    // Optional: Handle export completion
  };

  // Function to trigger Excel export
  const exportToExcel = () => {
    if (pivotRef.current) {
      pivotRef.current.excelExport();
    }
  };


  return (
    <>
    <style>
      {`
        /* Remove top margin/padding from AccumulationChartComponent */
        #pie-chart2,
        #pie-chart2 .e-accumulationchart,
        #pie-chart2 .e-accumulationchart-container {
          margin-top: 0px !important;
          padding-top: 0px !important;
          margin-left: 0px !important;
          padding-left: 0px !important;
        }
        /* Left align pie chart with padding for labels */
        #pie-chart2 {
          display: flex !important;
          justify-content: flex-start !important;
          align-items: flex-start !important;
          margin: 0 !important;
          padding: 0 10px 0 10px !important;
          overflow: visible !important;
        }
        #pie-chart2 .e-accumulationchart-container {
          display: flex !important;
          justify-content: flex-start !important;
          align-items: flex-start !important;
          margin: 0 auto 0 0 !important;
          padding: 0 !important;
        }
        #pie-chart2 .e-accumulationchart {
          margin: 0 auto 0 0 !important;
          padding: 0 !important;
        }
        #pie-chart2 svg {
          margin: 0 auto 0 0 !important;
          display: block !important;
        }
        /* Prevent label truncation in pie chart data labels */
        #pie-chart2 .e-accumulation-datalabel,
        #pie-chart2 .e-accumulation-datalabel text,
        #pie-chart2 .e-accumulation-datalabel tspan,
        #pie-chart2 .e-datalabel,
        #pie-chart2 .e-datalabel text,
        #pie-chart2 .e-datalabel tspan,
        #pie-chart2 text,
        #pie-chart2 tspan {
          text-overflow: clip !important;
          overflow: visible !important;
          white-space: nowrap !important;
          text-anchor: start !important;
          max-width: none !important;
          width: auto !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        /* Prevent any text truncation in SVG */
        #pie-chart2 svg text,
        #pie-chart2 svg tspan {
          text-overflow: clip !important;
          overflow: visible !important;
          white-space: nowrap !important;
          max-width: none !important;
          width: auto !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        /* Ensure all label groups are visible and on top */
        #pie-chart2 [class*="datalabel"],
        #pie-chart2 [class*="label"] {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 9999 !important;
          position: relative !important;
        }
        /* Ensure pie chart SVG and labels are on top of all controls */
        #pie-chart2 svg {
          overflow: visible !important;
          z-index: 9999 !important;
        }
        #pie-chart2 svg text,
        #pie-chart2 svg g[class*="label"],
        #pie-chart2 svg g[class*="datalabel"] {
          z-index: 10000 !important;
        }
        /* Pie chart tooltip on top of pie chart */
        #pie-chart2_tooltip,
        #pie-chart2_svg_tooltip,
        .e-tooltip-wrap,
        .e-accumulationchart-tooltip,
        .e-chart-tooltip,
        [id*="tooltip"] {
          z-index: 99999 !important;
        }
        /* Prevent label truncation in pie chart legend */
        #pie-chart2 .e-legend-text {
          text-overflow: clip !important;
          overflow: visible !important;
          white-space: normal !important;
          word-wrap: break-word !important;
        }
        #pie-chart2 .e-legend-item {
          overflow: visible !important;
        }
        /* Move legend to the right to prevent label truncation */
        #pie-chart2 .e-legend {
          margin-left: 60px !important;
          padding-left: 0px !important;
        }
        /* Ensure legend text has enough space */
        #pie-chart2 .e-legend-text {
          max-width: none !important;
          width: auto !important;
        }
        #FactPermitJobPivot table caption,
        #FactPermitJobPivot .e-gridheader table caption,
        #FactPermitJobPivot .e-pivotview table caption {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        /* Keep column headers in one row - prevent diagonal/rotated display */
        #FactPermitJobPivot .e-headercell,
        #FactPermitJobPivot .e-headercelldiv,
        #FactPermitJobPivot .e-valueheader .e-headercell,
        #FactPermitJobPivot .e-valueheader .e-headercelldiv,
        #FactPermitJobPivot .e-columnsheader .e-headercell,
        #FactPermitJobPivot .e-columnsheader .e-headercelldiv {
          transform: none !important;
          writing-mode: horizontal-tb !important;
          text-orientation: mixed !important;
          white-space: normal !important;
          text-align: center !important;
          vertical-align: middle !important;
        }
        /* Remove bold from column headers */
        #FactPermitJobPivot .e-columnsheader .e-headercell,
        #FactPermitJobPivot .e-columnsheader .e-headercelldiv,
        #FactPermitJobPivot .e-columnsheader th,
        #FactPermitJobPivot .e-columnsheader table thead tr th {
          font-weight: normal !important;
        }
        /* Prevent header rotation */
        #FactPermitJobPivot .e-headercelldiv span,
        #FactPermitJobPivot .e-headercell span {
          transform: none !important;
          writing-mode: horizontal-tb !important;
          display: inline-block !important;
        }
        /* Ensure headers are in single row */
        #FactPermitJobPivot .e-gridheader table thead tr {
          display: table-row !important;
        }
        #FactPermitJobPivot .e-gridheader table thead th {
          display: table-cell !important;
          vertical-align: middle !important;
        }
        /* Force all header rows to be on same level - merge into single row */
        #FactPermitJobPivot .e-gridheader table thead {
          display: table-header-group !important;
          position: relative !important;
        }
        /* Make all header rows appear at the same vertical position */
        #FactPermitJobPivot .e-gridheader table thead tr {
          display: table-row !important;
          position: relative !important;
        }
        /* Position all header rows at the same top position */
        #FactPermitJobPivot .e-gridheader table thead tr:not(:last-child) {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 1 !important;
        }
        #FactPermitJobPivot .e-gridheader table thead tr:last-child {
          position: relative !important;
          z-index: 2 !important;
        }
        /* Ensure column headers (statusName) are horizontal and visible */
        #FactPermitJobPivot .e-columnsheader,
        #FactPermitJobPivot .e-columnsheader th,
        #FactPermitJobPivot .e-columnsheader .e-headercell,
        #FactPermitJobPivot .e-columnsheader .e-headercelldiv {
          transform: none !important;
          writing-mode: horizontal-tb !important;
          text-orientation: mixed !important;
          white-space: normal !important;
          height: auto !important;
          line-height: normal !important;
          vertical-align: middle !important;
        }
        /* Force all header rows to have the same height - single row only */
        #FactPermitJobPivot .e-gridheader table {
          border-collapse: collapse !important;
        }
        #FactPermitJobPivot .e-gridheader table thead {
          height: 32px !important;
          max-height: 32px !important;
          display: table-header-group !important;
        }
        /* Hide all header rows except the last one */
        #FactPermitJobPivot .e-gridheader table thead tr:not(:last-child) {
          display: none !important;
          height: 0 !important;
          max-height: 0 !important;
          overflow: hidden !important;
        }
        /* Force the last header row to be exactly 55px */
        #FactPermitJobPivot .e-gridheader table thead tr:last-child {
          height: 55px !important;
          min-height: 55px !important;
          max-height: 55px !important;
          line-height: 16px !important;
          display: table-row !important;
        }
        /* Ensure all header cells are on same row level and same height */
        #FactPermitJobPivot .e-gridheader table thead tr:last-child th {
          position: relative !important;
          top: auto !important;
          transform: none !important;
          vertical-align: middle !important;
          height: 55px !important;
          min-height: 55px !important;
          max-height: 55px !important;
          padding: 1px 1px !important;
          box-sizing: border-box !important;
          text-align: right !important;
          font-weight: normal !important;
        }
        /* Ensure column header cells match value header cell height */
        #FactPermitJobPivot .e-columnsheader th,
        #FactPermitJobPivot .e-valueheader th,
        #FactPermitJobPivot .e-columnsheader table thead tr th,
        #FactPermitJobPivot .e-valueheader table thead tr th {
          height: 55px !important;
          min-height: 55px !important;
          max-height: 55px !important;
          box-sizing: border-box !important;
        }
        /* Ensure header cell content doesn't overflow */
        #FactPermitJobPivot .e-headercell,
        #FactPermitJobPivot .e-headercelldiv {
          height: 100% !important;
          max-height: 28px !important;
          overflow: hidden !important;
          line-height: 16px !important;
          display: block !important;
          text-align: right !important;
        }
        /* Align column header content to right */
        #FactPermitJobPivot .e-columnsheader .e-headercell,
        #FactPermitJobPivot .e-columnsheader .e-headercelldiv {
          text-align: right !important;
          display: block !important;
          font-weight: normal !important;
        }
        /* Keep value header content centered */
        #FactPermitJobPivot .e-valueheader .e-headercell,
        #FactPermitJobPivot .e-valueheader .e-headercelldiv {
          text-align: center !important;
          display: block !important;
        }
        /* Prevent any nested elements from increasing height */
        #FactPermitJobPivot .e-headercell *,
        #FactPermitJobPivot .e-headercelldiv * {
          max-height: 42px !important;
          line-height: 20px !important;
          font-size: 10px !important;
        }
        /* Set height of row handler cursor to 0 */
        #FactPermitJobPivot .e-rhandler.e-rcursor,
        #FactPermitJobPivot .e-rhandler,
        #FactPermitJobPivot .e-rcursor {
          height: 0 !important;
          min-height: 0 !important;
          max-height: 0 !important;
          display: none !important;
        }
        /* Ensure PivotViewComponent has auto height to fit all rows */
        #FactPermitJobPivot {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          margin-right: -40px !important;
        }
        #FactPermitJobPivot .e-pivotview {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        /* Ensure grid content expands to fit all rows */
        #FactPermitJobPivot .e-grid,
        #FactPermitJobPivot .e-gridcontent,
        #FactPermitJobPivot .e-content {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        #FactPermitJobPivot .e-gridcontent table,
        #FactPermitJobPivot .e-content table {
          height: auto !important;
        }
        /* Set line height of each row to 24px */
        #FactPermitJobPivot table tbody tr {
          line-height: 24px !important;
          height: auto !important;
          border-bottom: 1px solid #e0e0e0 !important;
        }
        #FactPermitJobPivot table tbody td,
        #FactPermitJobPivot .e-gridcell,
        #FactPermitJobPivot .e-valuescontent {
          line-height: 24px !important;
          padding: 2px 4px !important;
          border-bottom: 1px solid #e0e0e0 !important;
          border-right: 1px solid #e0e0e0 !important;
          text-align: right !important;
          vertical-align: middle !important;
        }
        /* Align row header cells to left */
        #FactPermitJobPivot .e-rowsheader th,
        #FactPermitJobPivot .e-rowsheader td,
        #FactPermitJobPivot table tbody td.e-rowsheader {
          text-align: left !important;
        }
        /* Ensure column headers have matching padding and alignment */
        #FactPermitJobPivot .e-columnsheader th,
        #FactPermitJobPivot .e-columnsheader table thead tr th {
          padding: 1px 1px !important;
          text-align: right !important;
          vertical-align: middle !important;
          font-weight: normal !important;
        }
        /* Ensure table columns align properly - use fixed layout */
        #FactPermitJobPivot table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        #FactPermitJobPivot table thead th,
        #FactPermitJobPivot table tbody td {
          padding-left: 4px !important;
          padding-right: 4px !important;
        }
        /* Match column widths between header and body */
        #FactPermitJobPivot .e-gridheader table,
        #FactPermitJobPivot .e-content table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        /* Ensure corresponding header and body cells have same width */
        #FactPermitJobPivot table thead th:nth-child(1),
        #FactPermitJobPivot table tbody td:nth-child(1),
        #FactPermitJobPivot .e-rowsheader th:nth-child(1),
        #FactPermitJobPivot .e-rowsheader td:nth-child(1),
        #FactPermitJobPivot table tbody td.e-rowsheader:nth-child(1),
        #FactPermitJobPivot .e-gridcell:nth-child(1),
        #FactPermitJobPivot .e-headercell:nth-child(1),
        #FactPermitJobPivot .e-gridheader th:nth-child(1),
        #FactPermitJobPivot .e-content td:nth-child(1) {
          width: 200px !important;
          min-width: 200px !important;
          max-width: 200px !important;
        }
        #FactPermitJobPivot table thead th:not(:nth-child(1)),
        #FactPermitJobPivot table tbody td:not(:nth-child(1)) {
          width: 60px !important;
        }
        /* Add border to header row as well */
        #FactPermitJobPivot table thead tr {
          border-bottom: 1px solid #e0e0e0 !important;
        }
        #FactPermitJobPivot table thead th {
          border-bottom: 1px solid #e0e0e0 !important;
          border-right: 1px solid #e0e0e0 !important;
        }
        /* Remove right border from last column */
        #FactPermitJobPivot table tbody td:last-child,
        #FactPermitJobPivot table thead th:last-child {
          border-right: none !important;
        }
        /* Pivot cell links: inherit text style, open in new tab */
        #FactPermitJobPivot table tbody td a {
          color: inherit !important;
          text-decoration: none !important;
          cursor: pointer !important;
        }
        #FactPermitJobPivot table tbody td a:hover {
          text-decoration: underline !important;
        }
        /* Rounded borders for dropdown lists */
        #month-dropdown,
        #company-dropdown {
          border-radius: 8px !important;
        }
        #month-dropdown .e-input-group,
        #company-dropdown .e-input-group {
          border-radius: 8px !important;
        }
        #month-dropdown .e-input-group input,
        #company-dropdown .e-input-group input {
          border-radius: 8px !important;
        }
        /* Year dropdown width */
        #year-dropdown {
          width: 100px !important;
          min-width: 100px !important;
          max-width: 100px !important;
        }
        #year-dropdown .e-input-group {
          width: 100px !important;
          min-width: 100px !important;
          max-width: 100px !important;
        }
      `}
    </style>
    <NavBar/>
    {loading && (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )}
    {error && (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Error loading data: {error}</Typography>
      </Box>
    )}
    {!loading && !error && (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 7, marginBottom: 2 }}>
          <Typography variant="h5" sx={{ marginLeft: 1, marginBottom: 0, fontWeight: 'bold', color: 'navy' }}>
            Permit Job Status By Weekly
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <label style={{ fontWeight: '500', marginRight: '8px', whiteSpace: 'nowrap', fontSize: '14px' }}>Select Year</label>
            <DropDownListComponent
              id="year-dropdown"
              dataSource={availableYears}
              fields={{ text: 'text', value: 'value' }}
              value={selectedYear}
              width="100px"
              placeholder="Select Year"
              change={(e) => setSelectedYear(e.value)}
              popupHeight="200px"
              style={{ width: '100px', }}
            />
            <Button
              variant="outlined"
              onClick={exportToExcel}
              sx={{ marginLeft: 2, marginTop: 0, textTransform: 'none', width: 140 }}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>
        {(!jobStatusData || jobStatusData.length === 0) ? (
          <Box sx={{ marginTop: 4, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
              No Data Available
            </Typography>
          </Box>
        ) : (
          <>
      <PivotViewComponent
          id="FactPermitJobPivot"
          ref={pivotRef}
          width="100%"
          allowExcelExport={true}
          excelExportComplete={excelExportComplete}
          gridSettings={{  
            allowResizing: true,
            showColumnSubTotals: true,
            showColumnGrandTotals: true,
            height: 'auto',
            headerCellInfo: onHeaderCellInfo,
            allowExcelExport: true,
          }}
          dataSourceSettings={{
            dataSource: transformedJobStatusData,
            expandAll: true,         
            showGrandTotals: true,
            showSubTotals: true,
            showRowGrandTotals: true,
            showColumnGrandTotals: true,
            showRowSubTotals: true,
            showColumnSubTotals: true,
            showValueCaption: false,
            valueAxis: 'column',       // values as columns
            rows: [
              { name: 'companyName', caption: 'Company' },
              { 
                name: 'weekName', 
                caption: 'Week',
                sortOrder: 'Ascending'
              },
            ],
            sortSettings: [
              {
                name: 'weekName',
                order: 'Ascending'
              }
            ],
            // no dimension columns – metrics appear as columns
            columns: [ { name: 'statusName', caption: 'Status' }],
            values: [ { name: 'count', caption: 'Count', format: 'N0', type: 'sum' }],
            filters: [],
            enableValueSorting: false,
            showHeaderWhenEmpty: false
          }}
        >
          <PivotInject services={[ExcelExport]} />
        </PivotViewComponent>
        {/* Dropdowns for Month and Company - always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, marginTop: 0, marginBottom: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', marginLeft: 2, alignItems: 'center', gap: 1 }}>
            <label style={{ fontWeight: '500', marginRight: '8px', whiteSpace: 'nowrap', fontSize: '14px' }}>Month</label>
            <DropDownListComponent
              id="month-dropdown"
              dataSource={availableMonths}
              fields={{ text: 'text', value: 'value' }}
              value={selectedMonth}
              placeholder="Select Month"
              change={(e) => setSelectedMonth(e.value)}
              popupHeight="300px"
              style={{ width: '120px', }}
            />
          </Box>
          {availableCompanies.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <label style={{ fontWeight: '500', marginRight: '8px', whiteSpace: 'nowrap', fontSize: '14px' }}>Company</label>
              <DropDownListComponent
                id="company-dropdown"
                dataSource={availableCompanies}
                fields={{ text: 'text', value: 'value' }}
                value={selectedCompany}
                placeholder="Select Company"
                change={(e) => setSelectedCompany(e.value)}
                popupHeight="300px"
                style={{ width: '400px', }}
              />
            </Box>
          )}
        </Box>
        {/* Pie chart section commented out
        {pieChartData && Array.isArray(pieChartData) && pieChartData.length > 0 && pieChartData.some(item => item.y > 0) ? (
          <Box sx={{ marginTop: 0, paddingTop: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'flex-start', paddingLeft: '60px', paddingRight: '60px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                <AccumulationChartComponent 
                id='pie-chart2' 
                load={loadChart} 
                width="700px"
                margin={{ left: 80, right: 80, top: 20, bottom: 20 }}
              legendSettings={{ 
                visible: true, 
                toggleVisibility: false, 
                position: 'Right', 
                textWrap: 'Wrap',
                width: '250px',
                paddingLeft: '40px',
                textStyle: {
                  textOverflow: 'none',
                  overflow: 'visible'
                }
              }} 
              enableBorderOnMouseMove={false} 
              selectionMode='Point' 
              tooltip={{ 
                enable: true, 
                format: '<b>${point.x}</b><br>Percentage: <b>${point.y}%</b>', 
                header: "", 
                enableHighlight: true 
              }} 
              height="350px"
            >
              <Inject services={[AccumulationLegend, PieSeries, AccumulationDataLabel, AccumulationTooltip, AccumulationSelection, Selection, ChartAnnotation, AccumulationAnnotation]} />
              <AccumulationSeriesCollectionDirective>
                <AccumulationSeriesDirective 
                  dataSource={pieChartData}
                  xName='x' 
                  yName='y' 
                  innerRadius='50%'
                  radius='70%' 
                  borderRadius={3} 
                  border={{ width: 1, color: '#ffffff' }} 
                  dataLabel={{ 
                    visible: true,
                    position: 'Outside',
                    name: 'label',
                    enableSmartLabels: false,
                    showZero: true,
                    connectorStyle: {
                      length: '30px',
                      type: 'Curve'
                    },
                    font: {
                      size: '9px'
                    }
                  }} 
                />
              </AccumulationSeriesCollectionDirective>
            </AccumulationChartComponent>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ marginTop: 2, paddingTop: 2, width: '100%', textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <h2><b>No data available</b></h2>
            </Typography>
            </Box>
          )}
        */}
          </>
        )}
      </>
    )}
    </>
  );
}
export default PermitJobWeeklyAnalytics;